export const CommunityAutoWelcomeMode = {
  NOTIFICATION: 'notification',
  POST: 'post',
  DM: 'dm',
} as const;

export type CommunityAutoWelcomeModeValue =
  (typeof CommunityAutoWelcomeMode)[keyof typeof CommunityAutoWelcomeMode];

export const COMMUNITY_WELCOME_CHECKLIST_KEYS = [
  'intro_post',
  'profile_complete',
  'first_comment',
] as const;

export type CommunityWelcomeChecklistKey = (typeof COMMUNITY_WELCOME_CHECKLIST_KEYS)[number];

export const COMMUNITY_AUTO_WELCOME_MESSAGE_MAX_CHARS = 2000;
export const COMMUNITY_WELCOME_DEFAULT_MESSAGE =
  'Welcome {{name}}! Glad you joined {{community}} — introduce yourself when you are ready.';

export const COMMUNITY_AUTO_WELCOME_POST_VARIANTS = ['announcement', 'pinned', 'standard'] as const;
export type CommunityAutoWelcomePostVariant =
  (typeof COMMUNITY_AUTO_WELCOME_POST_VARIANTS)[number];

export const COMMUNITY_WELCOME_CHECKLIST_STEP_META: Record<
  CommunityWelcomeChecklistKey,
  { label: string; description: string }
> = {
  intro_post: {
    label: 'Publish your intro post',
    description: 'Share a short hello with the community.',
  },
  profile_complete: {
    label: 'Complete your profile',
    description: 'Add a photo, headline, or bio so members recognize you.',
  },
  first_comment: {
    label: 'Leave your first comment',
    description: 'Reply to a post to join the conversation.',
  },
};

export type CommunityWelcomeChecklistItem = {
  key: CommunityWelcomeChecklistKey;
  label: string;
  description: string;
  done: boolean;
  href?: string | null;
};

export type CommunityWelcomeChecklistProgress = Record<CommunityWelcomeChecklistKey, boolean>;

function trimOrNull(value: string | null | undefined, max: number): string | null {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return null;
  return trimmed.slice(0, max);
}

export function normalizeCommunityAutoWelcomeMessage(
  value: string | null | undefined,
): string | null {
  return trimOrNull(value, COMMUNITY_AUTO_WELCOME_MESSAGE_MAX_CHARS);
}

export function interpolateCommunityAutoWelcomeMessage(
  template: string | null | undefined,
  params: {
    memberName: string;
    memberUsername?: string | null;
    communityTitle: string;
  },
): string {
  const source = template?.trim() || COMMUNITY_WELCOME_DEFAULT_MESSAGE;
  const name = params.memberName.trim() || params.memberUsername?.trim() || 'there';
  const mention = params.memberUsername?.trim() ? `@${params.memberUsername.trim()}` : name;
  return source
    .replaceAll('{{name}}', name)
    .replaceAll('{{username}}', params.memberUsername?.trim() || name)
    .replaceAll('{{mention}}', mention)
    .replaceAll('{{community}}', params.communityTitle.trim() || 'this Circle')
    .slice(0, COMMUNITY_AUTO_WELCOME_MESSAGE_MAX_CHARS);
}

export function isCommunityProfileCompleteForWelcomeChecklist(profile: {
  username?: string | null;
  displayName?: string | null;
  avatarUrl?: string | null;
  headline?: string | null;
  bio?: string | null;
} | null): boolean {
  if (!profile) return false;
  const hasName = Boolean(profile.displayName?.trim() || profile.username?.trim());
  const hasIdentitySignal = Boolean(
    profile.avatarUrl?.trim() || profile.headline?.trim() || profile.bio?.trim(),
  );
  return hasName && hasIdentitySignal;
}

export function normalizeCommunityWelcomeChecklistSteps(
  value: unknown,
): CommunityWelcomeChecklistKey[] {
  if (!Array.isArray(value) || value.length === 0) {
    return [...COMMUNITY_WELCOME_CHECKLIST_KEYS];
  }
  const allowed = new Set<string>(COMMUNITY_WELCOME_CHECKLIST_KEYS);
  const steps = value
    .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
    .filter((entry): entry is CommunityWelcomeChecklistKey => allowed.has(entry));
  return steps.length ? steps : [...COMMUNITY_WELCOME_CHECKLIST_KEYS];
}

export function normalizeCommunityAutoWelcomePostVariant(
  value: string | null | undefined,
): CommunityAutoWelcomePostVariant {
  const trimmed = (value ?? '').trim();
  if ((COMMUNITY_AUTO_WELCOME_POST_VARIANTS as readonly string[]).includes(trimmed)) {
    return trimmed as CommunityAutoWelcomePostVariant;
  }
  return 'announcement';
}

export function buildCommunityWelcomeChecklistItems(params: {
  progress: CommunityWelcomeChecklistProgress;
  enabledSteps?: CommunityWelcomeChecklistKey[];
  profileEditHref?: string | null;
  feedHref?: string | null;
}): CommunityWelcomeChecklistItem[] {
  const enabledSteps = params.enabledSteps ?? [...COMMUNITY_WELCOME_CHECKLIST_KEYS];
  return enabledSteps.map((key) => {
    const meta = COMMUNITY_WELCOME_CHECKLIST_STEP_META[key];
    return {
      key,
      label: meta.label,
      description: meta.description,
      done: params.progress[key] ?? false,
      href: key === 'profile_complete' ? params.profileEditHref : params.feedHref,
    };
  });
}

export function isCommunityWelcomeChecklistComplete(
  progress: CommunityWelcomeChecklistProgress,
  enabledSteps?: CommunityWelcomeChecklistKey[],
): boolean {
  const steps = enabledSteps ?? [...COMMUNITY_WELCOME_CHECKLIST_KEYS];
  return steps.every((key) => progress[key]);
}
